import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {Modal} from 'react-bootstrap';
import moment from 'moment';
import {usePopupCreateTransferContext} from '../contexts/PopupCreateTransferContext';
import {useTransfersByAgentContext} from '../contexts/TransfersByAgentContext';
import {useTransferCalculationContext} from '../contexts/TransferCalculationContext';
import {can} from '../../_common/components/Permissions';
import Parameters from './Parameters';
import Calculation from './Calculation';
import Lines from './Lines';
import CustomLines from './CustomLines';
import PopupWarning from './PopupWarning';
import PopupDocsWarning from './PopupDocsWarning';
import DataItemCheckbox from '../../_common/components/DataItemCheckbox';
import {getYearsOptions} from '../utils';
import styles from './styles.module.scss';

const PopupCreateTransfer = () => {
  const [show, setShow] = useState(false);
  const handleShowModal = () => setShow(true);
  const handleCloseModal = () => setShow(false);
  const isEnabled = can('full', 'transfersByAgentPage');
  const {
    formik,
    columns,
    tableData,
    newPaycheck,
    handleCreate,
    setAllReceiptsChecked,
    updateVat
  } = usePopupCreateTransferContext();
  const {agentId, fullName, isTaxAdjustmentOrForm101Absent, generalAdminNotes} = useTransfersByAgentContext();
  const dateValue = moment().format('DD-MM-YYYY').toString();
  const paycheckYear = formik.values.year ? formik.values.year.value : moment().year();
  const paycheckMonth = formik.values.month ? formik.values.month.value : moment().month() + 1;
  const paycheckValue = `${moment().month() + 1}.${paycheckYear}`;
  const {calculate} = useTransferCalculationContext();
  const calculation = useMemo(
    () => calculate(formik.values, newPaycheck, paycheckYear, paycheckMonth),
    [formik.values, newPaycheck, calculate, paycheckYear, paycheckMonth]
  );
  const prefix = 'transfersByAgent-popup';

  useEffect(() => {
    updateVat(calculation.vatValue);
  }, [calculation.vatValue]);

  const getCleanFullName = () => {
    if (fullName) {
      return fullName.includes('undefined') ? '' : fullName;
    } else return '';
  };

  return (
    <div>
      {isTaxAdjustmentOrForm101Absent
        ? <PopupDocsWarning actionType={'create'} handleAction={handleShowModal}/>
        : (
          <button
            className={styles.primary}
            disabled={!isEnabled}
            onClick={handleShowModal}
          >
            <FormattedMessage id={`${prefix}-create-button`} defaultMessage={'New transfer'}/>
          </button>
        )
      }
      <Modal show={show} onHide={handleCloseModal} dialogClassName={'extra-large-width'}>
        <Modal.Body className={styles.modalContainer}>
          <h3 className={styles.itemHeader}>
            <span className={'mx-1'}>
              <FormattedMessage id={`${prefix}-create-header-1`} defaultMessage={'Transferred to agent'}/>
            </span>
            <span className={'mx-1'}>
              <FormattedMessage
                id={`${prefix}-edit-create-header-2`}
                defaultMessage={'{agentId} - {agentName}'}
                values={{agentId, agentName: getCleanFullName()}}
              />
            </span>
            <span className={'mx-1'}>
              <FormattedMessage
                id={`${prefix}-edit-create-header-3`}
                defaultMessage={'{dateValue} (Paycheck {paycheckValue})'}
                values={{dateValue, paycheckValue}}
              />
            </span>
          </h3>
          <div>
            <Parameters
              formik={formik}
              getYearsOptions={getYearsOptions}
              newPaycheck={newPaycheck}
            />
            <div className={styles.tableSection}>
              <DataItemCheckbox
                fieldName={'allReceiptsChecked'}
                onChange={() => setAllReceiptsChecked()}
                inputClassname={'transfers-checkbox'}
                containerClassname={styles.checkAllCheckbox}
                checkboxSize={'transfers-checkbox-size'}
              />
              {useMemo(() => <Lines columns={columns} tableData={tableData}/>, [columns, tableData])}
            </div>
            <CustomLines values={formik.values}/>
            {useMemo(() => <Calculation formikValues={formik.values} {...calculation} />, [calculation])}
          </div>
          <div className={styles.actions}>
            <button
              className={styles.btnCancel}
              onClick={handleCloseModal}
            >
              <FormattedMessage id={'label-button-cancel'} defaultMessage={'Cancel'}/>
            </button>
            {formik.values.adminNotes || generalAdminNotes
              ? (
                <PopupWarning
                  generalNotes={generalAdminNotes}
                  transferNotes={formik.values.adminNotes}
                  disabled={!formik.isValid}
                  handleAction={() => {
                    handleCreate();
                    handleCloseModal();
                  }}
                />
              )
              : (
                <button
                  className={styles.btnSave}
                  disabled={!formik.isValid}
                  onClick={() => {
                    handleCreate();
                    handleCloseModal();
                  }}
                >
                  <FormattedMessage id={`${prefix}-warning-save`} defaultMessage={'Save transfer'}/>
                </button>
              )
            }
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PopupCreateTransfer;